import {Component, EventEmitter, Output} from '@angular/core';

// Simple theme switcher shown in the app header. Adds/removes body classes and updates CSS vars.
@Component({
    selector: 'theme-selector',
    template: `
    <div class="theme-selector ml-3">
      <button mat-icon-button class="select-theme" aria-label="Theme selection" [matMenuTriggerFor]="selectThemeMenu">
        <mat-icon>format_color_fill</mat-icon>
      </button>
      <mat-menu #selectThemeMenu>
        <button mat-menu-item value="dark-theme"
          class="theme dark"
          (click)="changeTheme('dark-theme')">
          @if (selectedTheme !== 'dark-theme') {
            <mat-icon>radio_button_unchecked</mat-icon>
          }
          @if (selectedTheme === 'dark-theme') {
            <mat-icon>radio_button_checked</mat-icon>
          }
          <span class="text">
            Standard
          </span>
        </button>
        <button mat-menu-item value="light-theme"
          class="theme light"
          (click)="changeTheme('light-theme')">
          @if (selectedTheme !== 'light-theme') {
            <mat-icon>radio_button_unchecked</mat-icon>
          }
          @if (selectedTheme === 'light-theme') {
            <mat-icon>radio_button_checked</mat-icon>
          }
          <span class="text">
            Lys
          </span>
        </button>
        <button mat-menu-item value="azure-theme"
          class="theme azure"
          (click)="changeTheme('azure-theme')">
          @if (selectedTheme !== 'azure-theme') {
            <mat-icon>radio_button_unchecked</mat-icon>
          }
          @if (selectedTheme === 'azure-theme') {
            <mat-icon>radio_button_checked</mat-icon>
          }
          <span class="text">
            Azure & Blå
          </span>
        </button>
        <button mat-menu-item value="cyan-theme"
          class="theme cyan"
          (click)="changeTheme('cyan-theme')">
          @if (selectedTheme !== 'cyan-theme') {
            <mat-icon>radio_button_unchecked</mat-icon>
          }
          @if (selectedTheme === 'cyan-theme') {
            <mat-icon>radio_button_checked</mat-icon>
          }
          <span class="text">
            Cyan & Orange
          </span>
        </button>
      </mat-menu>
    </div>
    `,
    styles: [`
    .select-theme {
      color: var(--sys-on-secondary)
    }
    .theme-selector {
      padding: 16px;
    }
    h3 {
      margin-bottom: 8px;
    }
    mat-button-toggle-group {
      display: flex;
      gap: 8px;
    }
    mat-button-toggle {
      transition: background-color 0.3s ease, color 0.3s ease;
    }
  `],
    standalone: false
})
export class ThemeSelectorComponent {
  // Tracks the currently active theme key (matches body class)
  selectedTheme: string = 'dark-theme'

  @Output() onThemeChange = new EventEmitter<string>()

  // Runtime palette used to set CSS variables for custom styles outside Material.
  currentPalette: { primary: string; primaryContrast: string; primaryRgb: string;
    accent: string; accentContrast: string; accentRgb: string } = {
    primary: '', primaryContrast: '', primaryRgb: '',
    accent: '', accentContrast: '', accentRgb: ''
  };

  // Map of theme keys to their brand/accent colors. These feed CSS variables like --primary-color.
  private themeColors = {
    'light-theme': {
      primary: '#45d172', primaryContrast: '#000000', primaryRgb: '69, 209, 113',
      accent: '#50504b', accentContrast: '#ffffff', accentRgb: '80, 80, 75'
    },
    'dark-theme': {
      primary: '#5ad759', primaryContrast: '#000000', primaryRgb: '90, 215, 89',
      accent: '#1e1e1e', accentContrast: '#e0e0e0', accentRgb: '30, 30, 30'
    },
    'azure-theme': {
      primary: '#3f51b5', primaryContrast: '#ffffff', primaryRgb: '63, 81, 181',
      accent: '#283593', accentContrast: '#ffffff', accentRgb: '40, 53, 147'
    },
    'cyan-theme': {
      primary: '#00bcd4', primaryContrast: '#000000', primaryRgb: '0, 188, 212',
      accent: '#ff9800', accentContrast: '#000000', accentRgb: '255, 152, 0'
    },
  };

  constructor() {
    // Restore last selection and apply on load.
    const currentTheme = localStorage.getItem('grant-admin.theme') ?? 'dark-theme';
    document.body.classList.add(currentTheme);
    this.changeTheme(currentTheme);
  }

  // Switch theme by toggling body classes and updating CSS custom properties.
  changeTheme(theme: string) {
    const themes = Object.keys(this.themeColors);
    themes.forEach(t => document.body.classList.remove(t));   // clear previous theme classes
    this.setPaletteByTheme(theme);                             // update runtime palette + vars
    this.selectedTheme = theme;
    document.body.classList.add(theme);                        // activate new theme class

    // Minimal set of Material system variables we want to override at runtime.
    const customerTheme = {
      primary: this.currentPalette.primary,
      secondary: this.currentPalette.accent,
    }
    const root = document.documentElement;

    // Apply theme variables to :root (override values provided by SCSS at build time)
    for (const [key, value] of Object.entries(customerTheme)) {
      if (key.includes('typography')) {
        root.style.setProperty(`--sys-${key}`, value.toString());
      } else {
        root.style.setProperty(`--sys-${key}`, value.toString());
      }
    }
    this.onThemeChange.emit(this.selectedTheme);
  }

  // Compute and persist the palette; also set convenience CSS vars used by app styles.
  setPaletteByTheme(theme: string): void {
    this.currentPalette = this.themeColors[theme];

    document.documentElement.style.setProperty('--primary-color', this.currentPalette.primary);
    document.documentElement.style.setProperty('--primary-contrast-color', this.currentPalette.primaryContrast);
    document.documentElement.style.setProperty('--primary-color-rgb', this.currentPalette.primaryRgb);

    document.documentElement.style.setProperty('--accent-color', this.currentPalette.accent);
    document.documentElement.style.setProperty('--accent-contrast-color', this.currentPalette.accentContrast);
    document.documentElement.style.setProperty('--accent-color-rgb', this.currentPalette.accentRgb);

    // Remember selection for next visit.
    localStorage.setItem('grant-admin.theme', theme);
  }
}
